import {
    AmbientLight,
    AxesHelper,
    CircleGeometry,
    Color,
    DoubleSide,
    GridHelper,
    Mesh,
    MeshBasicMaterial,
    MeshLambertMaterial,
    MOUSE,
    OrthographicCamera,
    PerspectiveCamera,
    PlaneGeometry,
    PointLight,
    Raycaster,
    Scene,
    Vector3,
    WebGLRenderer
} from "three";
import * as Globals from "./globals";
import {setDebugMode} from "./globals";
import {OrbitControls} from "./override/OrbitControls2";
import {copyLayersToNewCamera, disableLayer, enableAllLayers, setObjectLayer} from "./utils/layerUtils";

import {
    attachUIEvents,
    baseSceneGroup,
    buildingGroup,
    furnitureGroup,
    highlightsGroup,
    initBlocks,
    initializeGroups,
    layoutView,
    measurementsGroup,
    roomGroup,
    roomNamesGroup,
    resetShellMenu,
    scenesContainer,
    sceneView,
    tmpGroup
} from "./main";
import {TransactionManager} from "./TransactionManager";
import {openProject} from "./projectManager";
import {fitPerspectiveCameraToObject, zoomOrthographicCameraToObject} from "./utils/cameraUtils";

//Debug
let rendererStats = Globals.isDebug ? stats() : null;

//General
export let scene, intersectPlane, raycaster, containerSize, renderer;

//Camera/Controls
export let sceneCamera, layoutCamera, sceneControls, layoutControls;

export let continueRender = true;
export const setContinueRender = (isActive) => {
    continueRender = isActive;
};

let cameras = {
    "perspective-3d": undefined,
    "perspective-front": undefined,
    "perspective-back": undefined,
    "perspective-top": undefined,
    "perspective-left": undefined,
    "perspective-bottom": undefined,
    "perspective-right": undefined,
    "walkCam": undefined
};
export let views = [];
let activeSceneIndex = 1;

export async function initScene() {
    setDebugMode(document.querySelector('meta[name="isDebug"]').content);
    setupRayCaster();
    setupScene();
    initializeGroups();

    //Cameras
    setupLayoutViewCamera(layoutView, baseSceneGroup);
    setupRegularCamera(sceneView);
    setup2DViews(baseSceneGroup);

    setupRenderer(scenesContainer);

    calculateCanvasSizes();

    await initBlocks();

    addBasePlane(true, baseSceneGroup);
    addLighting(baseSceneGroup);
    addIntersectPlane();
    // if (Globals.isDebug) {
    addAxes(baseSceneGroup);
    // }

    attachUIEvents();

    //Projekt oder Testprojekt laden
    openProject(templateId);

    animate();
    TransactionManager.init();
}

//Base scene setup
export function setupRenderer(container) {
    containerSize = container.getBoundingClientRect();

    renderer = new WebGLRenderer({antialias: true, powerPreference: "high-performance"});
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(containerSize.width, containerSize.height);
    renderer.shadowMap.enabled = false;

    if (Globals.isDebug) {
        addPerformanceMonitor();
    }

    return renderer;
}

export function setupScene() {
    scene = new Scene();
    scene.background = Globals.sceneBackgroundColor;
}

export function animate() {
    if (continueRender) {
        render();
        setContinueRender(false);
    }
    requestAnimationFrame(animate);
}

export function render() {
    if (beforeSceneRender) {
        beforeSceneRender();
    }
    // renderer.shadowMap.enabled = false;
    views[0].render(); //Layout immer rendern
    views[activeSceneIndex].render(); //3D Szene nur im Konfigurator mit rendern

    if (Globals.isDebug) {
        rendererStats.update(renderer);
    }
}

let beforeSceneRender = null;

export function setBeforeSceneRender(callback) {
    beforeSceneRender = callback;
}

//Scene functions
export function calculateCanvasSizes() {
    layoutView.width = layoutView.clientWidth * window.devicePixelRatio;
    layoutView.height = layoutView.clientHeight * window.devicePixelRatio;
    let layoutRatio = layoutView.width / layoutView.height;
    layoutCamera.aspect = layoutRatio;
    layoutCamera.top = layoutCamera.right / layoutRatio;
    layoutCamera.bottom = layoutCamera.left / layoutRatio;
    sceneView.width = sceneView.clientWidth * window.devicePixelRatio;
    sceneView.height = sceneView.clientHeight * window.devicePixelRatio;

    if (activeSceneIndex !== 1) {
        let sceneRatio = sceneView.width / sceneView.height;
        sceneCamera.aspect = sceneRatio;
        sceneCamera.top = sceneCamera.right / sceneRatio;
        sceneCamera.bottom = sceneCamera.left / sceneRatio;
    }
}

function View(canvas, camera, controls, name) {
    canvas.width = canvas.clientWidth * window.devicePixelRatio;
    canvas.height = canvas.clientHeight * window.devicePixelRatio;
    let context = canvas.getContext('2d');

    this.camera = camera;
    this.controls = controls;
    this.name = name;

    this.render = function () {
        if (canvas.clientWidth === 0) {
            return;
        }
        renderer.setSize(canvas.clientWidth, canvas.clientHeight);
        camera.aspect = canvas.clientWidth / canvas.clientHeight;
        camera.updateProjectionMatrix();
        controls.update();
        renderer.render(scene, camera);
        context.drawImage(renderer.domElement, 0, 0);
    };
}

//Camera Setup
export const perspectiveCameraAngles = {
    frontRight: new Vector3(5, 2, 5),
    frontLeft: new Vector3(-5, 2, 5),
    backRight: new Vector3(5, 2, -5),
    backLeft: new Vector3(-5, 2, -5)
};

export function setupRegularCamera(canvas) {
    let camera = new PerspectiveCamera(75, sceneView.clientWidth / sceneView.clientHeight, 0.2, 100); //Camera
    camera.name = "perspective-3d";
    let position = new Vector3(5, 2, 5);
    camera.position.set(position.x, position.y, position.z);

    let controls = new OrbitControls(camera, sceneView); //Regular controls
    controls.mouseButtons = {
        LEFT: MOUSE.ROTATE, MIDDLE: null, RIGHT: MOUSE.PAN
    };
    controls.target = new Vector3(0, 0, 0);
    controls.minDistance = 1;
    controls.maxDistance = 50;

    cameras["perspective-3d"] = {"camera": camera, "controls": controls, "position": position, "index": 1};
    sceneCamera = camera;
    sceneControls = controls;
    enableAllLayers(sceneCamera);
    disableLayer(sceneCamera, Globals.layers.hidden);
    disableLayer(sceneCamera, Globals.layers.layoutViewOnly); //Disable
    disableLayer(sceneCamera, Globals.layers.roomShapes); //Disable
    disableLayer(sceneCamera, Globals.layers.roomNames); //Disable
    views.push(new View(canvas, sceneCamera, sceneControls, "perspective-3d"));
}

export function setupLayoutViewCamera(canvas, group) {
    let aspectRatio = canvas.width / canvas.height;
    let width = 15;
    let height = width / aspectRatio;

    let camera = new OrthographicCamera(width / -2, width / 2, height / 2, height / -2, 0.1, 50);
    camera.name = "fixedTopCam";
    let position = new Vector3(0, 25, 0);
    camera.position.set(position.x, position.y, position.z);

    let controls = addCameraOrbitControls(camera, layoutView);
    group.addWithUuid(camera);
    camera.lookAt(0, 0, 0);
    layoutCamera = camera;
    layoutControls = controls;

    enableAllLayers(layoutCamera);
    camera.layers.disable(Globals.layers.roof);
    camera.layers.disable(Globals.layers.floor);
    camera.layers.disable(Globals.layers.raehm);
    camera.layers.disable(Globals.layers.hidden);
    camera.layers.disable(Globals.layers.roomShapes);
    camera.layers.disable(Globals.layers.roomNames);
    camera.layers.disable(Globals.layers.sceneViewOnly);

    views.push(new View(canvas, layoutCamera, layoutControls, "layout"));
}

export function setup2DViews(group) {
    setup2DView("perspective-top", new Vector3(0, 25, 0), group, 2);
    setup2DView("perspective-bottom", new Vector3(0, -25, 0), group, 3);
    setup2DView("perspective-front", new Vector3(0, 0, 25), group, 4);
    setup2DView("perspective-back", new Vector3(0, 0, -25), group, 5);
    setup2DView("perspective-left", new Vector3(-25, 0), group, 6);
    setup2DView("perspective-right", new Vector3(25, 0), group, 7);
}

function setup2DView(name, position, group, index) {
    let aspectRatio = sceneView.clientWidth / sceneView.clientHeight;
    let width = 25;
    let height = width / aspectRatio;

    let camera = new OrthographicCamera(width / -2, width / 2, height / 2, height / -2, 0.1, 1000);
    camera.name = name;
    camera.position.set(position.x, position.y, position.z);
    camera.lookAt(0, 0, 0);

    //Controls for orthographic views without rotation
    let controls = addCameraOrbitControls(camera, sceneView);
    group.addWithUuid(camera);
    cameras[name] = {"camera": camera, "controls": controls, "position": position, "index": index};
    views.push(new View(sceneView, camera, controls, name));
    disableLayer(camera, Globals.layers.hidden);
}

//Camera utils
function addCameraOrbitControls(camera, element) {
    let controls = new OrbitControls(camera, element);
    controls.enableRotate = false;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.object = camera;
    controls.mouseButtons = {LEFT: null, MIDDLE: null, RIGHT: MOUSE.PAN};
    controls.minZoom = 0.2;
    controls.maxZoom = 7.5;

    return controls;
}

export function getFixedPerspectiveCameraPosition(name) {
    switch (name) {
        case "perspective-3d-front-right":
            return perspectiveCameraAngles.frontRight;
        case "perspective-3d-front-left":
            return perspectiveCameraAngles.frontLeft;
        case "perspective-3d-back-right":
            return perspectiveCameraAngles.backRight;
        case "perspective-3d-back-left":
            return perspectiveCameraAngles.backLeft;
        default:
            return perspectiveCameraAngles.frontRight;
    }
}

export function activateCameraByName(name, position = null) {
    let tmpName = name;
    let fixedPerspectiveCamera = false;
    if (name.includes("3d")) {
        fixedPerspectiveCamera = name !== "perspective-3d";
        name = "perspective-3d";
    }

    if (cameras[name] !== undefined) {
        let previousCamera = sceneCamera;
        sceneCamera = cameras[name]["camera"];
        sceneControls = cameras[name]["controls"];
        let pos = position === null ? cameras[name]["position"] : position;

        scene.activeCamera = sceneCamera;
        if (fixedPerspectiveCamera) {
            pos = getFixedPerspectiveCameraPosition(tmpName);
        }
        scene.activeCamera.position.copy(pos);
        scene.activeCamera.lookAt(0, 0, 0);
        sceneControls.target = new Vector3(0, 0, 0);
        copyLayersToNewCamera(sceneCamera, previousCamera);
        activeSceneIndex = cameras[name]["index"];
        calculateCanvasSizes();
        if (!name.includes("3d")) {
            zoomOrthographicCameraToObject(sceneCamera, buildingGroup, 1.1);
        } else {
            fitPerspectiveCameraToObject(sceneCamera, sceneControls, buildingGroup, 1.3);
        }
        render();
    } else {
        console.error("Camera isn't defined");
    }
}


export function setupRayCaster() {
    raycaster = new Raycaster();
    let threshold = 0.01;
    raycaster.params.Mesh.threshold = threshold;
    raycaster.params.Points.threshold = threshold;
    raycaster.params.Line.threshold = threshold;
    raycaster.params.LOD.threshold = threshold;
    raycaster.params.Sprite.threshold = threshold;
    raycaster.layers.enableAll();
}

//Add scene default objects
export function addLighting(group) {
    group.addWithUuid(new AmbientLight(0xeeeeee, 0.7));

    let light = new PointLight(0xffffff, 0.6);
    light.position.set(-10, 25, 10);
    // light.castShadow = true;
    group.addWithUuid(light);
}

export function addBasePlane(rasterPlane, group) {
    //Add base plane
    if (!rasterPlane) {
        let geometry = new CircleGeometry(25, 64);
        geometry.rotateX(Math.PI / 2);
        let plane = new Mesh(geometry, new MeshLambertMaterial({
            color: Globals.sceneBackgroundColor, side: DoubleSide
        }));
        plane.position.set(0, -0.01, 0);
        // plane.receiveShadow = true;
        plane.name = "basePlane";
        group.addWithUuid(plane);
    } else {
        const divisions = 100;
        const size = 0.545 * divisions; //Single element width
        let gridHelper = new GridHelper(size, divisions, 0xbbbbbb, 0xcfcfcf);
        gridHelper.name = "gridHelper";
        group.addWithUuid(gridHelper);
    }
}

export function addIntersectPlane() {
    intersectPlane = new Mesh(new PlaneGeometry(500, 500), new MeshBasicMaterial({
        color: 0x248f24, alphaTest: 0, visible: false
    }));
    intersectPlane.name = "intersectPlane";
    intersectPlane.rotateX(-Math.PI / 2);
    scene.addWithUuid(intersectPlane);
}

export function addAxes(group) {
    let axesHelper = new AxesHelper(3);
    axesHelper.name = "axesHelper";
    axesHelper.setColors(new Color(1, 0, 0), new Color(0, 0, 1), new Color(0, 1, 0));
    axesHelper.scale.set(1, 1, -1);
    group.addWithUuid(axesHelper);//Axes
    setObjectLayer(axesHelper, Globals.layers.layoutViewOnly, true);
}

//Utils
export function clearScene() {
    for (let i = scene.children.length - 1; i >= 0; i--) {
        let child = scene.children[i];
        //Löscht nur Elemente außerhalb der baseSceneGroup
        if (child.name !== "baseSceneGroup" && child.name !== "intersectPlane" && child.name !== "highlightingGroup") {
            clearGroup(child);
            try {
                child.geometry.dispose();
            } catch {
            }
            try {
                child.material.dispose();
            } catch {
            }
            scene.remove(child);
        }
    }
}

export function resetScene() {
    clearGroup(buildingGroup);
    clearGroup(furnitureGroup);
    clearGroup(highlightsGroup);
    clearGroup(measurementsGroup);
    clearGroup(roomNamesGroup);
    clearGroup(roomGroup);
    clearGroup(tmpGroup);
    TransactionManager.init();
    resetShellMenu();
}

export function clearGroup(group) {
    if (group.children === undefined) {
        return;
    }

    //Gruppe rekursiv leeren und dispose Objekte & Materialien
    while (group.children.length > 0) {
        let object = group.children[0];
        if (object.children.length > 0) {
            clearGroup(object);
        }
        group.remove(object);
        try {
            object.geometry.dispose();
        } catch {
        }
        try {
            // object.material.dispose();
        } catch {
        }
        try {
            object.dispose();
        } catch {
        }
    }
}

export function readScene() {
    scene.children.forEach(function (child) {
        console.log(child);
    });
}


//Scene statistics
function addPerformanceMonitor() {
    if (rendererStats === null) {
        rendererStats = stats();
    }
    rendererStats.domElement.style.position = 'absolute';
    rendererStats.domElement.style.left = '135px';
    rendererStats.domElement.style.bottom = '0px';
    rendererStats.domElement.style.zIndex = '2';
    document.body.appendChild(rendererStats.domElement);
}

function stats() {
    const container = document.createElement('div');
    container.style.cssText = 'width:80px;opacity:0.9;cursor:pointer';

    const msDiv = document.createElement('div');
    msDiv.style.cssText = 'padding:0 0 3px 3px;text-align:left;background-color:#200;';
    container.appendChild(msDiv);

    const msText = document.createElement('div');
    msText.style.cssText = 'color:#f00;font-family:Helvetica,Arial,sans-serif;font-size:9px;font-weight:bold;line-height:15px';
    msText.innerHTML = 'WebGLRenderer';
    msDiv.appendChild(msText);

    const msTexts = [];
    const nLines = 8;
    for (let i = 0; i < nLines; i++) {
        msTexts[i] = document.createElement('div');
        msTexts[i].style.cssText = 'color:#f00;background-color:#311;font-family:Helvetica,Arial,sans-serif;font-size:9px;font-weight:bold;line-height:15px';
        msDiv.appendChild(msTexts[i]);
        msTexts[i].innerHTML = '-';
    }


    let lastTime = performance.now();
    const lastFrames = 0;
    let frameRate = 0;
    return {
        domElement: container,

        update: function (webGLRenderer) {
            // sanity check
            console.assert(webGLRenderer instanceof WebGLRenderer);

            // refresh only 30time per second
            if (performance.now() - lastTime < 1000 / 30) return;
            frameRate = (webGLRenderer.info.render.frame - lastFrames) / (performance.now() - lastTime / 1000).toFixed(2);
            lastTime = performance.now();

            let i = 0;
            msTexts[i++].textContent = "== Memory =====";
            msTexts[i++].textContent = "Geometries: " + webGLRenderer.info.memory.geometries;
            msTexts[i++].textContent = "Textures: " + webGLRenderer.info.memory.textures;
            msTexts[i++].textContent = "== Render =====";
            msTexts[i++].textContent = "Calls: " + webGLRenderer.info.render.calls;
            msTexts[i++].textContent = "Points: " + webGLRenderer.info.render.points;
            msTexts[i++].textContent = "Lines: " + webGLRenderer.info.render.lines;
            msTexts[i++].textContent = "Triangles: " + webGLRenderer.info.render.triangles;
        }
    };
}
